import { useEffect, useState } from 'react'
import {
  Avatar,
  Button,
  Flex,
  Input,
  Popconfirm,
  Select,
  Table,
  Tag,
  Text,
  message,
  tableActionsCell,
} from '@nula/core-ui'
import * as Sentry from '@sentry/react'
import { ApiError } from '@/lib/http/client'
import {
  listMembers,
  listPendingInvitations,
  removeMember,
  revokeInvitation,
  setMemberRole,
  updateWorkspace,
  inviteByEmail,
} from '@/lib/workspace/api/api'
import { useWorkspaceStore } from '@/stores/use-workspace-store'
import { canDeleteWorkspace } from '../../../utils/permissions'
import type {
  WorkspaceInfo,
  WorkspaceMember,
  WorkspaceRole,
  MemberDefaultPermission,
  InvitationRole,
  PendingInvitation,
} from '@/lib/workspace/types/types'
import { isLinkInvitation } from '@/lib/workspace/types/types'
import { TransferOwnershipModal } from '../transfer-ownership-modal'
import styles from './members-tab.module.css'

interface MembersTabProps {
  workspace: WorkspaceInfo
}

type MemberRow =
  | { kind: 'member'; data: WorkspaceMember }
  | { kind: 'invitation'; data: PendingInvitation }

export function MembersTab({ workspace }: MembersTabProps) {
  const [members, setMembers] = useState<WorkspaceMember[]>([])
  const [invitations, setInvitations] = useState<PendingInvitation[]>([])
  const [transferOpen, setTransferOpen] = useState(false)
  const [pendingPermission, setPendingPermission] = useState<MemberDefaultPermission>(
    workspace.memberDefaultPermission,
  )
  // Email invite
  const [emailValue, setEmailValue] = useState('')
  const [emailRole, setEmailRole] = useState<InvitationRole>('member')
  const [emailSending, setEmailSending] = useState(false)
  const [emailSent, setEmailSent] = useState<string | null>(null)

  const refresh = useWorkspaceStore((s) => s.refreshFromServer)
  const isOwner = canDeleteWorkspace(workspace.role)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      listMembers(workspace.id),
      listPendingInvitations(workspace.id),
    ])
      .then(([m, inv]) => {
        if (cancelled) return
        setMembers(m)
        setInvitations(inv)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        console.error('[MembersTab] load failed:', err)
        Sentry.captureException(err)
        message.error('Failed to load members.')
      })
    return () => { cancelled = true }
  }, [workspace.id])

  // NOTE: self-demote / self-remove (acting on member.userId === current user)
  // is intentionally allowed by these handlers and the server. The current
  // workspace.role on the prop will go stale until the next workspace store
  // refresh — accepted decision. Do not gate on "self" here without
  // re-opening that thread.
  const handleRoleChange = async (member: WorkspaceMember, newRole: WorkspaceRole) => {
    if (newRole === 'owner') return
    try {
      await setMemberRole(workspace.id, member.userId, newRole)
      setMembers((m) => m.map((x) => (x.userId === member.userId ? { ...x, role: newRole } : x)))
    } catch (err) {
      console.error('[MembersTab] role change failed:', err)
      Sentry.captureException(err)
      message.error('Could not change role.')
    }
  }

  const handleRemove = async (member: WorkspaceMember) => {
    try {
      await removeMember(workspace.id, member.userId)
      setMembers((m) => m.filter((x) => x.userId !== member.userId))
    } catch (err) {
      console.error('[MembersTab] remove failed:', err)
      Sentry.captureException(err)
      message.error('Could not remove member.')
    }
  }

  const handleRevoke = async (inv: PendingInvitation) => {
    try {
      await revokeInvitation(workspace.id, inv.id)
      setInvitations((list) => list.filter((x) => x.id !== inv.id))
    } catch (err) {
      console.error('[MembersTab] revoke failed:', err)
      Sentry.captureException(err)
      message.error('Could not revoke invitation.')
    }
  }

  const handlePermissionChange = async (value: MemberDefaultPermission) => {
    setPendingPermission(value)
    try {
      await updateWorkspace(workspace.id, { memberDefaultPermission: value })
      await refresh()
    } catch (err) {
      setPendingPermission(workspace.memberDefaultPermission)
      console.error('[MembersTab] permission update failed:', err)
      Sentry.captureException(err)
      message.error('Could not update default permission.')
    }
  }

  const handleEmailSend = async () => {
    const normalized = emailValue.trim().toLowerCase()
    if (!normalized) return
    setEmailSending(true)
    try {
      await inviteByEmail(workspace.id, normalized, emailRole)
      setEmailSent(normalized)
      setEmailValue('')
      // Re-fetch invitations so the new row appears immediately in the table
      const updated = await listPendingInvitations(workspace.id)
      setInvitations(updated)
    } catch (err: unknown) {
      console.error('[MembersTab] email invite failed:', err)
      Sentry.captureException(err)
      if (err instanceof ApiError && err.status === 403) {
        message.error("Email's domain isn't in the allowed list.")
      } else {
        message.error(err instanceof ApiError ? err.message : 'Failed to send invite.')
      }
    } finally {
      setEmailSending(false)
    }
  }

  const rows: MemberRow[] = [
    ...members.map((m): MemberRow => ({ kind: 'member', data: m })),
    ...invitations.map((inv): MemberRow => ({ kind: 'invitation', data: inv })),
  ]

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_: unknown, row: MemberRow) => {
        if (row.kind === 'invitation') {
          const label = isLinkInvitation(row.data) ? 'Anyone with link' : (row.data.email ?? '')
          return (
            <Flex align="center" gap="sm" className={styles.nameCell}>
              <Avatar size="sm" alt={label}>{label.slice(0, 1).toUpperCase() || '?'}</Avatar>
              <Text type="secondary">{label}</Text>
            </Flex>
          )
        }
        const m = row.data
        return (
          <Flex align="center" gap="sm" className={styles.nameCell}>
            <Avatar size="sm" src={m.avatarUrl ?? undefined} alt={m.name ?? m.email}>
              {(m.name ?? m.email).slice(0, 1).toUpperCase()}
            </Avatar>
            <Text>{m.name ?? m.email}</Text>
          </Flex>
        )
      },
    },
    {
      title: 'Email',
      key: 'email',
      render: (_: unknown, row: MemberRow) => (
        row.kind === 'member'
          ? <Text>{row.data.email}</Text>
          : <Text type="secondary">{row.data.email ?? '—'}</Text>
      ),
    },
    {
      title: 'Role',
      key: 'role',
      render: (_: unknown, row: MemberRow) => {
        if (row.kind === 'invitation') {
          return <Tag color="accent">Pending</Tag>
        }
        const m = row.data
        if (m.role === 'owner') return <Tag>Owner</Tag>
        return (
          <Select
            size="sm"
            value={m.role}
            label="Role"
            options={[
              { value: 'admin', label: 'Admin' },
              { value: 'member', label: 'Member' },
            ]}
            onChange={(v) => handleRoleChange(m, v as WorkspaceRole)}
          />
        )
      },
    },
    {
      title: '',
      key: 'actions',
      render: (_: unknown, row: MemberRow) => {
        if (row.kind === 'invitation') {
          return (
            <div className={tableActionsCell}>
              <Popconfirm
                title="Revoke invitation"
                description={`Revoke this invitation${row.data.email ? ` to ${row.data.email}` : ''}?`}
                okText="Revoke"
                okDanger
                onConfirm={() => handleRevoke(row.data)}
              >
                <Button variant="text">Revoke</Button>
              </Popconfirm>
            </div>
          )
        }
        const m = row.data
        return (
          <div className={tableActionsCell}>
            {m.role !== 'owner' && (
              <Popconfirm
                title="Remove member"
                description={`Remove ${m.name ?? m.email} from this workspace?`}
                okText="Remove"
                okDanger
                onConfirm={() => handleRemove(m)}
              >
                <Button variant="text">Remove</Button>
              </Popconfirm>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <Flex vertical gap="lg">
      {/* ── Tab header ───────────────────────────────────────────────────── */}
      <Flex vertical gap="xs">
        <Text size="md" className={styles.tabTitle}>Members</Text>
        <Text size="sm" type="secondary">Manage who has access to this workspace.</Text>
      </Flex>

      {/* ── Invite by email ───────────────────────────────────────────────── */}
      <Flex vertical gap="sm" className={styles.inviteCard}>
        <Text className={styles.sectionLabel}>Invite by email</Text>
        {emailSent ? (
          <Flex vertical gap="xs">
            <Text size="sm" type="secondary">Invite sent to {emailSent}.</Text>
            <Button size="sm" variant="text" onClick={() => setEmailSent(null)}>Send another</Button>
          </Flex>
        ) : (
          <Flex gap="xs" justify="space-between">
            <Flex gap="xs">
              <Input
                variant="default"
                size="sm"
                value={emailValue}
                onChange={(e) => setEmailValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && emailValue.trim() && !emailSending) void handleEmailSend() }}
                label="Email"
                className={styles.emailInput}
              />
              <Select
                size="sm"
                variant="default"
                value={emailRole}
                options={[
                  { value: 'member', label: 'Member' },
                  { value: 'admin', label: 'Admin' },
                ]}
                onChange={(v) => setEmailRole(v as InvitationRole)}
                className={styles.roleSelect}
              />
            </Flex>
            <Button
              size="sm"
              onClick={handleEmailSend}
              disabled={!emailValue.trim() || emailSending}
              loading={emailSending}
            >
              Invite
            </Button>
          </Flex>
        )}
      </Flex>

      {/* ── Default member permission ─────────────────────────────────────── */}
      <Flex vertical gap="sm">
        <Text className={styles.sectionLabel}>Default member permission</Text>
        <Flex gap="sm">
          {([
            { value: 'read',  label: 'Read',  desc: 'Can view but not edit.' },
            { value: 'edit',  label: 'Edit',  desc: 'Can edit but not delete or manage permissions.' },
            { value: 'admin', label: 'Admin', desc: 'Can manage and delete any workspace content.' },
          ] as { value: MemberDefaultPermission; label: string; desc: string }[]).map(({ value, label, desc }) => (
            <button
              key={value}
              type="button"
              className={styles.permCard}
              data-active={pendingPermission === value}
              onClick={() => handlePermissionChange(value)}
            >
              <Text size="sm" className={styles.permCardLabel}>{label}</Text>
              <Text size="xs" type="secondary">{desc}</Text>
            </button>
          ))}
        </Flex>
      </Flex>

      {/* ── Members + pending invitations ────────────────────────────────── */}
      <Flex vertical gap="sm">
        <Text className={styles.sectionLabel}>Members ({rows.length})</Text>
        <Flex className={styles.tableScroll}>
          <Table
            rowKey={(row) => (row as MemberRow).kind === 'member' ? `m-${(row as MemberRow & { kind: 'member' }).data.userId}` : `i-${(row as MemberRow & { kind: 'invitation' }).data.id}`}
            dataSource={rows as unknown as Record<string, unknown>[]}
            columns={columns as unknown as Parameters<typeof Table>[0]['columns']}
            pagination={false}
          />
        </Flex>
        {isOwner && (
          <Button variant="text" onClick={() => setTransferOpen(true)}>
            Transfer ownership…
          </Button>
        )}
      </Flex>

      {isOwner && (
        <TransferOwnershipModal
          workspace={workspace}
          members={members}
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
        />
      )}
    </Flex>
  )
}
